/* eslint-disable react/no-unknown-property --
 * Lint rule errors on react-three-fiber primitives. Disabling solely for this
 * file. TypeScript should still catch an invalid props.
 */

import React, { useEffect, useImperativeHandle, useRef } from "react";
import { Button, styled, Typography } from "@mui/material";
import { TimerSand } from "mdi-material-ui";
import { Center } from "~/components/Center";
import { ErrorMessage } from "~/components/error-message";
import type { Group } from "~/lib/three";
import {
  CameraActions,
  CameraControls,
  Canvas,
  eventsFactory,
  Matrix4,
  Quaternion,
  TransformControls,
  useThree,
  Vector3,
} from "~/lib/three";
import { usePlayerActions } from "../../actions";
import { LoadingFeedback, PanelLayout, TopicName } from "../../components";
import { usePlayerConfig } from "../../config";
import type { ThreeDPanel } from "../../panels";
import { RecordStoreError } from "../../record-store";
import { performGpuPick } from "./gpu-picking";
import type { TransformNode } from "./transforms";
import type { ThreeDObject, ThreeDRecords } from "./use-three-d-records";
import { useThreeDRecords } from "./use-three-d-records";

interface SceneActions {
  onDoubleClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const StyledCanvas = styled(Canvas)({
  touchAction: "none",
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
});

// Studio is going to use its own event handling instead of relying on r3f's
// events. The camera and transform controls attach their own native event
// listeners instead of using r3f's event system so they'll continue to work.
const events: typeof eventsFactory = (store) => {
  const initialEvents = eventsFactory(store);

  return {
    ...initialEvents,
    enabled: false,
  };
};

export function ThreeDVisualization({ panel }: { panel: ThreeDPanel }) {
  const { threeDInteractivityIndicator } = usePlayerConfig();

  const playerActions = usePlayerActions();

  const [threeDSnapshot, isPlaceholderSnapshot] = useThreeDRecords({ panel });

  function handleDisableStaticTransforms(): void {
    playerActions.toggleStaticTransforms(panel, false);
  }

  const sceneRef = useRef<SceneActions>(null);

  let content;
  if (threeDSnapshot.status === "pending") {
    content = <LoadingFeedback description="3D data" />;
  } else if (threeDSnapshot.status === "rejected") {
    if (
      threeDSnapshot.reason instanceof RecordStoreError &&
      threeDSnapshot.reason.topic?.name === "/tf_static"
    ) {
      content = (
        <ErrorMessage disableTypography>
          <ErrorMessage.Paragraph>
            Unable to load <TopicName monospace>/tf_static</TopicName> records
          </ErrorMessage.Paragraph>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleDisableStaticTransforms}
          >
            Disable static transforms
          </Button>
        </ErrorMessage>
      );
    } else {
      content = <ErrorMessage>An error occurred</ErrorMessage>;
    }
  } else {
    const { value } = threeDSnapshot;

    content = (
      <>
        <StyledCanvas
          frameloop="demand"
          camera={{
            // Position camera behind and above origin, looking towards
            // positive Z.
            position: [-5, 5, -5],
          }}
          events={events}
          // Get the browser event from the container (not an r3f event) and
          // pass it to the scene to actually perform raycasting. Components
          // rendered below the <Canvas /> use r3f's event system so the
          // double click has to be captured at or above the <Canvas />.
          onDoubleClick={(e) => sceneRef.current?.onDoubleClick(e)}
        >
          <PanelScene ref={sceneRef} value={value} />
        </StyledCanvas>
        {value.firstTimestamp != null && (
          <Center sx={{ position: "absolute", left: 0, top: 0 }}>
            <TimerSand fontSize="large" />
            <Typography variant="h5" component="p" paragraph>
              No recent point cloud
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => playerActions.seek(value.firstTimestamp!)}
            >
              Skip to First Point Cloud
            </Button>
          </Center>
        )}
      </>
    );
  }

  return (
    <PanelLayout>
      {content}
      {isPlaceholderSnapshot ? (
        <LoadingFeedback description="3D data" />
      ) : threeDSnapshot.status === "fulfilled" ? (
        threeDInteractivityIndicator
      ) : null}
    </PanelLayout>
  );
}

type CameraControlsRefTarget = React.ComponentRef<typeof CameraControls>;

// Describes the rotation from Three's X-left, Y-up, Z-forward coordinates
// to ROS' conventional X-forward, Y-left, Z-up coordinates.
// prettier-ignore
const threeToRosTransform = new Matrix4(
  0, 1, 0, 0,
  0, 0, 1, 0,
  1, 0, 0, 0,
  0, 0, 0, 1,
);
const threeToRosRotation = new Quaternion().setFromRotationMatrix(
  threeToRosTransform,
);

const PanelScene = React.forwardRef<SceneActions, { value: ThreeDRecords }>(
  function PanelScene({ value }, ref) {
    const gl = useThree((state) => state.gl);
    const invalidate = useThree((state) => state.invalidate);
    const camera = useThree((state) => state.camera);

    const { showRotationControls, objects, transforms } = value;

    const controlsRef = useRef<CameraControlsRefTarget>(null);
    const groupRef = useRef<Group>(null);

    useImperativeHandle(
      ref,
      () => ({
        onDoubleClick(e) {
          if (groupRef.current == null || controlsRef.current == null) {
            return;
          }

          const pickResult = performGpuPick(
            gl,
            camera,
            groupRef.current,
            objects,
            e,
          );

          if (pickResult == null) {
            return;
          }

          const { mesh, pointIndex } = pickResult;

          const localPoint = new Vector3().fromBufferAttribute(
            mesh.geometry.getAttribute("position"),
            pointIndex,
          );
          const worldPoint = mesh.localToWorld(localPoint);

          // Calculate the translation from the clicked point (which needs to be
          // converted to the <group>'s local coordinates) to the origin and
          // negate it to give the translation that must be applied to move that
          // point in the <group> to the world's origin.
          const translation = groupRef.current
            .worldToLocal(worldPoint.clone())
            .negate();

          groupRef.current.position.x = translation.x;
          groupRef.current.position.y = translation.y;
          groupRef.current.position.z = translation.z;

          // Look back at the world's origin.
          controlsRef.current.setFocalOffset(0, 0, 0);

          invalidate();
        },
      }),
      [gl, objects, camera, invalidate],
    );

    // The camera controls library sometimes gets into a state where it
    // recognizes you're trying to control the camera but doesn't actually
    // update anything until the next frame. Since we're using on-demand frame
    // rendering, that next frame never comes. To avoid this unresponsiveness,
    // just invalidate the scene as necessary to trigger another frame.
    useEffect(() => {
      function handleControlStart(): void {
        invalidate();
      }

      const { current: controls } = controlsRef;

      controls?.addEventListener("controlstart", handleControlStart);

      return () => {
        controls?.removeEventListener("controlstart", handleControlStart);
      };
    }, [invalidate]);

    const sceneHasObjects = objects.length > 0;

    return (
      <>
        <TransformControls
          mode="rotate"
          showX={showRotationControls && sceneHasObjects}
          showY={showRotationControls && sceneHasObjects}
          showZ={showRotationControls && sceneHasObjects}
        >
          <group ref={groupRef}>
            <group quaternion={threeToRosRotation}>
              {renderObjects(objects, transforms)}
            </group>
          </group>
        </TransformControls>
        <CameraControls
          ref={controlsRef}
          makeDefault
          smoothTime={0}
          draggingSmoothTime={0}
          mouseButtons={{
            left: CameraActions.ROTATE,
            // Offset the camera instead of trucking it to keep the controls'
            // center of rotation the same regardless of how the user pans.
            right: CameraActions.OFFSET,
            middle: CameraActions.DOLLY,
            wheel: CameraActions.DOLLY,
          }}
          touches={{
            one: CameraActions.TOUCH_ROTATE,
            // Offset the camera instead of trucking it to keep the controls'
            // center of rotation the same regardless of how the user pans.
            two: CameraActions.TOUCH_DOLLY_OFFSET,
            three: CameraActions.NONE,
          }}
        />
      </>
    );
  },
);

function renderObjects(
  objects: ReadonlyArray<ThreeDObject>,
  transforms: ReadonlyArray<TransformNode> | null,
) {
  if (transforms == null) {
    return objects.map(renderObject);
  } else {
    const renderedObjects = new Set<ThreeDObject>();

    const frames = transforms.map((node) =>
      renderFrame(node, objects, renderedObjects),
    );

    return (
      <>
        {frames}
        {objects.flatMap((object) => {
          if (renderedObjects.has(object)) {
            return [];
          } else {
            return renderObject(object);
          }
        })}
      </>
    );
  }
}

function renderObject(object: ThreeDObject) {
  return (
    <points key={object.id} geometry={object.data.data}>
      <pointsMaterial vertexColors size={object.size} sizeAttenuation={false} />
    </points>
  );
}

function renderFrame(
  node: TransformNode,
  objects: ReadonlyArray<ThreeDObject>,
  renderedObjects: Set<ThreeDObject>,
) {
  const frameObject = objects.find(
    (object) => object.data.frameId === node.frameId,
  );

  if (frameObject != null) {
    renderedObjects.add(frameObject);
  }

  return (
    <group
      key={node.frameId}
      quaternion={[
        node.rotation.x,
        node.rotation.y,
        node.rotation.z,
        node.rotation.w,
      ]}
      position={[node.translation.x, node.translation.y, node.translation.z]}
    >
      {frameObject != null && renderObject(frameObject)}
      {node.children.map((child) =>
        renderFrame(child, objects, renderedObjects),
      )}
    </group>
  );
}
